import { Container, IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { gsap } from "gsap";
import { IListInMpNotificationController } from "../controllers/IListInMpNotificationController";
import { BaseScalableView } from "../../../common/BaseScalableView";
import { IFocusable } from "../../../layers/IFocusable";
import { CommonResourcesPackage } from "../../../common/resources/CommonResourcesPackage";
import { ILogger } from "../../../../logger/ILogger";
import { FadeScreen } from "../../../game/components/FadeScreen";
import { ButtonWithScaleAnimation } from "../../../common/components/ButtonWithScaleAnimation";
import { PromiseCompletionSource } from "../../../../../shared/async/PromiseCompletionSource";
import { ICanvasScaler } from "../../../../../canvas/ICanvasScaler";
import { FullscreenInputBlocker } from "../../../game/components/FullscreenInputBlocker";
import { GameFont } from "../../../common/GameFont";
import { ListInMpNotificationResourcePackage } from "../resources/ListInMpNotificationResourcePackage";
import { TextWithUnderline } from "../../../game/components/TextWithUnderline";

export class ListInMpNotificationPopupView extends BaseScalableView<void> implements IFocusable {
    private static readonly DESCRIPTION_TEXT = "TO LIST THIS BUILDING\nIN THE IN-GAME MARKETPLACE,\nYOU FIRST NEED TO TRANSFER IT\nTO YOUR IN-GAME WALLET.\nFOR DETAILED INSTRUCTIONS,";
    private static readonly CLICK_HERE_TEXT = "PLEASE CLICK HERE";

    public readonly focusable: boolean = true;

    private readonly commonResources: CommonResourcesPackage;
    private readonly listNotificationResources: ListInMpNotificationResourcePackage;

    private readonly listNotificationController: IListInMpNotificationController;
    private readonly logger: ILogger;
    private readonly viewAnimation: gsap.core.Timeline;

    private fadeScreen: FadeScreen;
    private background: Sprite;
    private header: Sprite;
    private descriptionText: Text;
    private clickHere: TextWithUnderline;
    private frame: Container;
    private closeButton: ButtonWithScaleAnimation;
    private topLight: Sprite;
    private leftLight: Sprite;
    private rightLight: Sprite;


    public constructor(
        operationControls: PromiseCompletionSource<void>,
        commonResources: CommonResourcesPackage,
        listNotificationResources: ListInMpNotificationResourcePackage,
        canvasScaler: ICanvasScaler,
        listNotificationController: IListInMpNotificationController,
        logger: ILogger
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.listNotificationResources = listNotificationResources;
        this.listNotificationController = listNotificationController;
        this.logger = logger;
        this.viewAnimation = new gsap.core.Timeline({ paused: true });
        this.viewAnimation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.3, ease: "power1.in" });
        this.clickHereHandle = this.clickHereHandle.bind(this);
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public start(): void {
        this.show();
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";

        this.topLight = new Sprite(this.listNotificationResources.assets_views_list_in_mp_notification_light_png);
        this.header = new Sprite(this.listNotificationResources.assets_views_list_in_mp_notification_logo_png);
        this.frame = new Container();
        const frameBg = new Sprite(this.listNotificationResources.assets_views_list_in_mp_notification_frame_png);

        this.descriptionText = new Text(ListInMpNotificationPopupView.DESCRIPTION_TEXT, new WhiteTextStyle());
        this.clickHere = new TextWithUnderline(ListInMpNotificationPopupView.CLICK_HERE_TEXT, new BlueTextStyle(), 0, -3);
        this.clickHere.eventMode = "static";
        this.clickHere.on("pointerup", this.clickHereHandle);

        this.closeButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_x_png);
        this.closeButton.hitArea = new Rectangle(-this.closeButton.width * 0.5, -this.closeButton.height, this.closeButton.width * 2, this.closeButton.height * 2);
        this.closeButton.onUp.add(this.closeButtonClickHandle, this);

        this.leftLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightLight = new Sprite(this.commonResources.assets_common_ui_lights_light_left_png);
        this.rightLight.scale.x = -1;
        this.background = new Sprite(this.listNotificationResources.assets_views_list_in_mp_notification_background_png);

        this.frame.addChild(frameBg, this.descriptionText, this.clickHere);
        this.addChild(this.fadeScreen, this.topLight, this.header, this.frame, this.closeButton, this.leftLight, this.rightLight, this.background);
    }

    private async clickHereHandle(): Promise<void> {
        try {
            this.clickHere.eventMode = "none";
            this.listNotificationController.openHowToExportInGameAssets();
            this.clickHere.eventMode = "static";
        } catch (error: unknown) {
            this.logger.error(error);
        }
    }

    private async closeButtonClickHandle(): Promise<void> {
        await this.hideView();
        this.operationControls.complete();
    }

    private async show(): Promise<void> {
        this.fadeScreen.fadeIn();
        await this.viewAnimation.play();
    }

    private async hideView(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        await this.viewAnimation.reverse();
    }

    private layout(width: number, _height: number): void {
        this.header.position.set(170, 440);
        this.frame.position.set(57, 828);
        this.descriptionText.position.set(91, 106);
        this.clickHere.position.set(197, 406);
        this.closeButton.position.set(375, 1423);
        this.leftLight.position.set(0, 920);
        this.rightLight.position.set(width, 920);
        this.background.position.set(0, 345);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.viewAnimation?.kill();
        this.closeButton.onUp.remove(this.closeButtonClickHandle, this);
        this.clickHere.off("pointerup", this.clickHereHandle);
        super.destroy(options);
    }
}

class WhiteTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 56,
            leading: 5
        }));
    }
}

class BlueTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#00F9FC",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 56
        }));
    }
}
